import { useMemo } from 'react';
import cn from 'classnames';
import { useSelect } from 'hooks/useSelect';
import { ISelectOption } from 'interfaces';

import {
  TreeSelectDropdown,
  TreeSelectDropdownProps,
} from './TreeSelectDropdown';
import { TreeSelectField, TreeSelectFieldProps } from './TreeSelectField';
import { Theme } from './types';
import { getOptionTreeData } from './utils';

type TreeSelectProps<T, P> =
  | DefaultTreeSelectProps<T, P>
  | WithEmptyTreeSelectProps<T, P>;

type DefaultTreeSelectProps<T, P> = {
  empty?: true;
} & CommonSelectProps<T | null, P>;

type WithEmptyTreeSelectProps<T, P> = {
  empty: false;
} & CommonSelectProps<T, P>;

interface CommonSelectProps<T, P> {
  value: T;
  options: ISelectOption<T, P>[];
  placeholder?: string;
  searchPlaceholder?: string;
  unknownOptionPlaceholder?: string;
  disabled?: boolean;
  search?: boolean;
  theme?: Theme;
  classNames?: {
    container?: string;
    text?: TreeSelectFieldProps['classNames'];
    dropdown?: TreeSelectDropdownProps<T, P>['classNames'];
  };
  onSelect: (value: T, option: ISelectOption<T, P>) => void;
}

export const TreeSelect = <T, P>({
  value,
  options,
  placeholder,
  searchPlaceholder,
  unknownOptionPlaceholder,
  disabled,
  search,
  empty = true,
  theme,
  classNames,
  onSelect,
}: TreeSelectProps<T, P>) => {
  const {
    containerRef,
    isOpen,
    selectedOptionLabel,
    handleIsOpenToggle,
    handleSelect,
  } = useSelect<any, P, HTMLDivElement>({
    value,
    options,
    placeholder,
    unknownOptionPlaceholder,
    disabled,
    onSelect,
  });

  const optionTreeData = useMemo(
    () => getOptionTreeData<any, P>({ value, options, empty }),
    [value, options]
  );

  return (
    <div ref={containerRef} className={cn('relative', classNames?.container)}>
      <TreeSelectField
        value={selectedOptionLabel}
        isOpen={isOpen}
        theme={theme}
        classNames={classNames?.text}
        onClick={handleIsOpenToggle}
      />
      {isOpen && (
        <TreeSelectDropdown<T, P>
          data={optionTreeData}
          placeholder={searchPlaceholder}
          search={search}
          classNames={{
            ...classNames?.dropdown,
            container: cn(
              'absolute z-[1] w-full mt-1',
              classNames?.dropdown?.container
            ),
          }}
          onSelect={handleSelect}
        />
      )}
    </div>
  );
};
